import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface ProgramListItem {
  program_id: number
  program_name: string
  thumbnail_url: string | null
  capacity: number
  location: string | null
  apply_start: string | null
  apply_end: string | null
  schedule_text: string | null
  has_sessions: boolean
  accepts_online_application: number
  is_visible: number
  is_closed: number
}

const PAGE_SIZE = 6

export default function ProgramList() {
  const navigate = useNavigate()
  const [programs, setPrograms] = useState<ProgramListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  useEffect(() => {
    fetchPrograms()
  }, [page])

  const fetchPrograms = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('is_visible', '1')
      params.append('skip', String((page - 1) * PAGE_SIZE))
      params.append('limit', String(PAGE_SIZE))

      const res = await fetch(getApiUrl(`/marubit/api/program/list?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setPrograms(data.programs)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const getPageNumbers = () => {
    const start = Math.max(1, page - 2)
    const end = Math.min(totalPages, start + 4)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">프로그램 안내 및 신청</h1>
          <p className="text-[14px] text-gray-400">총 {total}건</p>
        </div>

        {/* 목록 */}
        {programs.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-300 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            등록된 프로그램이 없습니다.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {programs.map(program => (
              <div
                key={program.program_id}
                onClick={() => navigate(`/program/${program.program_id}`)}
                className="bg-white rounded-2xl overflow-hidden f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)] cursor-pointer hover:shadow-[0_4px_20px_rgba(112,48,160,0.15)] transition-shadow flex items-center gap-5 p-4"
              >
                {/* 썸네일 */}
                <div className="w-[80px] h-[100px] shrink-0 bg-gray-100 rounded-[10px] overflow-hidden flex items-center justify-center border border-gray-100">
                  {program.thumbnail_url ? (
                    <img
                      src={program.thumbnail_url}
                      alt={program.program_name}
                      className="w-full h-full object-contain p-1"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-gray-300">
                      <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="currentColor" strokeWidth={1.5}>
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* 정보 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-[15px] font-bold text-[#222] truncate">{program.program_name}</p>
                    {program.accepts_online_application === 0 ? (
                      <span className="shrink-0 px-2 py-[2px] text-[11px] font-semibold bg-[#fdf8f0] text-[#8a7568] rounded-full">안내</span>
                    ) : program.is_closed === 1 && (
                      <span className="shrink-0 px-2 py-[2px] text-[11px] font-semibold bg-gray-100 text-gray-400 rounded-full">마감</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-gray-400">
                    {program.location && (
                      <span className="flex items-center gap-1">
                        📍 {program.location}
                      </span>
                    )}
                    {program.capacity > 0 && (
                      <span className="flex items-center gap-1">
                        👥 {program.capacity}명
                      </span>
                    )}
                    {program.schedule_text && (
                      <span className="flex items-center gap-1">
                        🗓 {program.schedule_text}
                      </span>
                    )}
                    {program.apply_start && program.apply_end ? (
                      <span className="flex items-center gap-1">
                        📅 신청 {formatDate(program.apply_start)} ~ {formatDate(program.apply_end)}
                      </span>
                    ) : program.accepts_online_application === 1 && (
                      <span className="flex items-center gap-1">
                        📅 상시 신청
                      </span>
                    )}
                  </div>
                </div>

                {/* 신청 버튼 */}
                <div className="shrink-0">
                  {program.accepts_online_application === 0 ? (
                    <span className="px-4 py-2 text-[13px] font-semibold text-[#8a7568] bg-[#fdf8f0] border border-[#e8d8ce] rounded-lg">
                      자세히 보기
                    </span>
                  ) : program.is_closed === 1 ? (
                    <span className="px-4 py-2 text-[13px] font-semibold text-gray-400 bg-gray-100 rounded-lg">
                      마감
                    </span>
                  ) : (
                    <span className="px-4 py-2 text-[13px] font-semibold text-white bg-[#887267] rounded-lg hover:bg-[#6b5248] transition-colors">
                      신청하기
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ‹
            </button>
            {getPageNumbers().map(num => (
              <button
                key={num}
                onClick={() => setPage(num)}
                className={[
                  'w-9 h-9 flex items-center justify-center rounded-lg text-[14px] font-medium transition-colors',
                  num === page
                    ? 'bg-[#887267] text-white border border-[#887267]'
                    : 'border border-gray-200 text-gray-500 hover:border-[#887267] hover:text-[#887267]',
                ].join(' ')}
              >
                {num}
              </button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  )
}